import React, { useMemo } from 'react';
import type { IPhase } from '~/types/estimates';
import { digitFormatter } from '~/utils';
import {
  computeEtaiementCostBreakdown,
  ETAIEMENT_COST_CATEGORY_LABELS,
  ETAIEMENT_COST_CATEGORY_ORDER,
  type EtaiementCostCategory,
} from '~/utils/etaiement-cost-breakdown';
import styles from './cost-breakdown-chart.module.scss';

const CATEGORY_COLORS: Record<EtaiementCostCategory, string> = {
  prestation: '#6AA287',
  transport: '#49517B',
  etudes: '#1B2559',
  location: '#14AE5C',
  autres: '#717A97',
};

const CHART_SIZE = 160;
const CHART_CENTER = CHART_SIZE / 2;
const CHART_RADIUS = 60;
const CHART_INNER_RADIUS = 36;

type PieSlice = {
  category: EtaiementCostCategory;
  value: number;
  percentage: number;
  startAngle: number;
  endAngle: number;
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number,
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

const describeDonutArc = (
  startAngle: number,
  endAngle: number,
  outerRadius: number,
  innerRadius: number,
): string => {
  const startOuter = polarToCartesian(CHART_CENTER, CHART_CENTER, outerRadius, startAngle);
  const endOuter = polarToCartesian(CHART_CENTER, CHART_CENTER, outerRadius, endAngle);
  const startInner = polarToCartesian(CHART_CENTER, CHART_CENTER, innerRadius, endAngle);
  const endInner = polarToCartesian(CHART_CENTER, CHART_CENTER, innerRadius, startAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
};

const buildPieSlices = (
  categories: Record<EtaiementCostCategory, number>,
  totalHT: number,
): PieSlice[] => {
  let currentAngle = 0;

  const sortedCategories = ETAIEMENT_COST_CATEGORY_ORDER
    .filter((category) => categories[category] > 0)
    .sort((a, b) => {
      const diff = categories[b] - categories[a];
      if (diff !== 0) return diff;
      return ETAIEMENT_COST_CATEGORY_ORDER.indexOf(a) - ETAIEMENT_COST_CATEGORY_ORDER.indexOf(b);
    });

  return sortedCategories.map((category) => {
    const value = categories[category];
    const percentage = totalHT > 0 ? (value / totalHT) * 100 : 0;
    const sweepAngle = totalHT > 0 ? (value / totalHT) * 360 : 0;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sweepAngle;
    currentAngle = endAngle;

    return {
      category,
      value,
      percentage,
      startAngle,
      endAngle,
    };
  });
};

const CostBreakdownChart = ({ phases }: { phases: IPhase[] }) => {
  const breakdown = useMemo(
    () => computeEtaiementCostBreakdown(phases),
    [phases],
  );

  const slices = useMemo(
    () => buildPieSlices(breakdown.categories, breakdown.totalHT),
    [breakdown],
  );

  const ariaLabel = slices.length > 0
    ? slices.map(
      (slice) => `${ETAIEMENT_COST_CATEGORY_LABELS[slice.category]}: ${slice.percentage.toFixed(1)} %`,
    ).join(', ')
    : 'Aucun coût calculé';

  return (
    <div className={styles.container}>
      <h4>Répartition des coûts</h4>
      {breakdown.totalHT <= 0
        ? <p className={styles.empty}>Aucun coût calculé</p>
        : <>
          <div className={styles.chartWrapper}>
            <svg
              className={styles.chart}
              width={CHART_SIZE}
              height={CHART_SIZE}
              viewBox={`0 0 ${CHART_SIZE} ${CHART_SIZE}`}
              role="img"
              aria-label={ariaLabel}
            >
              {slices.length === 1
                ? <>
                  <circle
                    cx={CHART_CENTER}
                    cy={CHART_CENTER}
                    r={CHART_RADIUS}
                    fill={CATEGORY_COLORS[slices[0].category]}
                  />
                  <circle
                    cx={CHART_CENTER}
                    cy={CHART_CENTER}
                    r={CHART_INNER_RADIUS}
                    fill="#FAFBFF"
                  />
                </>
                : slices.map((slice) => (
                  <path
                    key={slice.category}
                    d={describeDonutArc(
                      slice.startAngle,
                      slice.endAngle,
                      CHART_RADIUS,
                      CHART_INNER_RADIUS,
                    )}
                    fill={CATEGORY_COLORS[slice.category]}
                  />
                ))
              }
            </svg>
            <p className={styles.total}>
              <span>Total HT</span>
              {digitFormatter.format(breakdown.totalHT)} €
            </p>
          </div>
          <ul className={styles.legend}>
            {slices.map((slice) => (
              <li key={slice.category} className={styles.legendItem}>
                <span
                  className={styles.legendColor}
                  style={{ backgroundColor: CATEGORY_COLORS[slice.category] }}
                />
                <span className={styles.legendLabel}>
                  {ETAIEMENT_COST_CATEGORY_LABELS[slice.category]}
                </span>
                <span className={styles.legendValue}>
                  {digitFormatter.format(slice.value)} €
                </span>
                <span className={styles.legendPercent}>
                  {slice.percentage.toFixed(1)} %
                </span>
              </li>
            ))}
          </ul>
        </>
      }
    </div>
  );
};

export default CostBreakdownChart;
